import { Document, Packer, Paragraph, ImageRun, TextRun, AlignmentType, VerticalAlign, Table, TableRow, TableCell, WidthType, BorderStyle, HeightRule, ShadingType } from "docx";
import { fetchImageWithDimensions, fitImage, FULL_WIDTH } from "./common.js";

// Front Pages
import { createFPTemp1 } from "./templates/frontPages/FPTemp1.js";
import { createFPTemp2 } from "./templates/frontPages/FPTemp2.js";
import { createFPTemp3 } from "./templates/frontPages/FPTemp3.js";
import { createFPTemp4 } from "./templates/frontPages/FPTemp4.js";

// TOC
import { createTOCTemp1 } from "./templates/TableOfContent/TOCTemp1.js";
import { createTOCTemp2 } from "./templates/TableOfContent/TOCTemp2.js";

// Org Chart
import { createOrgChartTemp1 } from "./templates/orgChart/OrgChartTemp1.js";
import { createOrgChartTemp2 } from "./templates/orgChart/OrgChartTemp2.js";
import { createOrgChartTemp3 } from "./templates/orgChart/OrgChartTemp3.js";

// Resumes
import { createResumeTemp1 } from "./templates/resumes/ResumeTemp1.js";
import { createResumeTemp2 } from "./templates/resumes/ResumeTemp2.js";
import { createResumeTemp3 } from "./templates/resumes/ResumeTemp3.js";
import { createResumeTemp4 } from "./templates/resumes/ResumeTemp4.js";

// Comparable Projects
import { createCPTemp1 } from "./templates/comparableProjects/CPTemp1.js";
import { createCPTemp2 } from "./templates/comparableProjects/CPTemp2.js";
import { createCPTemp3 } from "./templates/comparableProjects/CPTemp3.js";
import { createCPTemp4 } from "./templates/comparableProjects/CPTemp4.js";

// Approach
import { createApprTemp1 } from "./templates/approach/ApprTemp1.js";
import { createApprTemp2 } from "./templates/approach/ApprTemp2.js";
import { createApprTemp3 } from "./templates/approach/ApprTemp3.js";

// Last Pages
import { createLPTemp1 } from "./templates/lastPages/LPTemp1.js";
import { createLPTemp2 } from "./templates/lastPages/LPTemp2.js";
import { createLPTemp3 } from "./templates/lastPages/LPTemp3.js";
import { createLPTemp4 } from "./templates/lastPages/LPTemp4.js";

import {
    isFrontPageReady,
    isTOCReady,
    isOrgChartReady,
    isResumeReady,
    isComparableReady,
    isApproachReady,
    isLastPageReady
} from "../proposalValidation.js";

// Helper to add custom pages (PDFs converted to images or just images)
const addCustomPages = async (customPages, position, startPageOffset, style = {}) => {
    const sections = [];
    if (!customPages || !Array.isArray(customPages)) return sections;

    // Constants for Layout
    const BANNER_HEIGHT_DXA = 800; // ~40 points
    const TEXT_COLOR = "FFFFFF";
    const ROW_BODY_HEIGHT = 15220; // Adjusted to fit larger banners on A4 (~16840 dxa total)

    const files = customPages.filter(f => f.position === position);
    for (const file of files) {
        // file.preview contains array of base64 images (pages) for PDFs
        if (file.isPDF && file.preview && Array.isArray(file.preview) && file.preview.length > 0) {
            for (const pageImg of file.preview) {
                try {
                    const imgData = await fetchImageWithDimensions(pageImg);
                    if (!imgData) continue;
                    const aspectRatio = imgData.width / imgData.height;
                    const targetHeight = Math.min(FULL_WIDTH / aspectRatio, ROW_BODY_HEIGHT - 250);
                    const imgRun = fitImage(imgData, FULL_WIDTH, targetHeight, "cover");

                    // Colors for this specific file
                    const headerColor = (file.bannerHeaderColor || style.colorScheme?.header || "545454").replace('#', '');
                    const footerColor = (file.bannerFooterColor || style.colorScheme?.footer || "545454").replace('#', '');

                    const layoutTable = new Table({
                        width: { size: 100, type: WidthType.PERCENTAGE },
                        borders: {
                            top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                            left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                            insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE },
                        },
                        rows: [
                            // 1. Top Banner
                            new TableRow({
                                height: { value: BANNER_HEIGHT_DXA, rule: HeightRule.EXACT },
                                children: [
                                    new TableCell({
                                        shading: { fill: headerColor, type: ShadingType.CLEAR },
                                        verticalAlign: VerticalAlign.CENTER,
                                        children: [
                                            new Paragraph({
                                                alignment: AlignmentType.CENTER,
                                                children: [
                                                    new TextRun({
                                                        text: file.bannerTitle || "",
                                                        color: TEXT_COLOR,
                                                        bold: true,
                                                        size: 28,
                                                        font: "Helvetica"
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            }),

                            // 2. Body (Image)
                            new TableRow({
                                height: { value: ROW_BODY_HEIGHT, rule: HeightRule.EXACT },
                                children: [
                                    new TableCell({
                                        verticalAlign: VerticalAlign.CENTER,
                                        children: [
                                            new Paragraph({
                                                alignment: AlignmentType.CENTER,
                                                children: [imgRun],
                                            })
                                        ]
                                    })
                                ]
                            }),

                            // 3. Bottom Banner
                            new TableRow({
                                height: { value: BANNER_HEIGHT_DXA, rule: HeightRule.EXACT },
                                children: [
                                    new TableCell({
                                        shading: { fill: footerColor, type: ShadingType.CLEAR },
                                        verticalAlign: VerticalAlign.BOTTOM,
                                        children: startPageOffset !== undefined ? [
                                            new Paragraph({
                                                alignment: AlignmentType.RIGHT,
                                                indent: { right: 200 },
                                                spacing: { after: 200, },
                                                children: [
                                                    new TextRun({
                                                        text: String(startPageOffset).padStart(2, '0'),
                                                        color: TEXT_COLOR,
                                                        bold: true,
                                                        size: 22,
                                                        font: "Helvetica"
                                                    })
                                                ]
                                            })
                                        ] : []
                                    })
                                ]
                            })
                        ]
                    });

                    sections.push({
                        properties: {
                            page: { margin: { top: 0, right: 0, bottom: 0, left: 0 } },
                            verticalAlign: VerticalAlign.TOP
                        },
                        children: [layoutTable]
                    });

                    if (startPageOffset !== undefined) {
                        startPageOffset++;
                    }
                } catch (e) {
                    console.error("Failed to add PDF page to Word", e);
                }
            }
        }
    }
    return { sections, nextOffset: startPageOffset };
};


export const generateWordProposal = async (proposal, pageStartMap = {}) => {
    const sections = [];

    // --- Front Page ---
    if (proposal.frontPage && isFrontPageReady(proposal.frontPage)) {
        if (proposal.frontPage.template === 1) sections.push(await createFPTemp1(proposal.frontPage));
        else if (proposal.frontPage.template === 2) sections.push(await createFPTemp2(proposal.frontPage));
        else if (proposal.frontPage.template === 3) sections.push(await createFPTemp3(proposal.frontPage));
        else if (proposal.frontPage.template === 4) sections.push(await createFPTemp4(proposal.frontPage));
    }

    // --- TOC & Front Page Extras ---
    const hasFPExtras = (proposal.customPages || []).some(f => f.position === 'frontPage');
    if ((proposal.toc && isTOCReady(proposal.toc)) || (proposal.frontPage && isFrontPageReady(proposal.frontPage)) || hasFPExtras) {
        if (proposal.toc && isTOCReady(proposal.toc)) {
            if (proposal.toc.template === 1) sections.push(...(await createTOCTemp1(proposal.toc)));
            else if (proposal.toc.template === 2) sections.push(...(await createTOCTemp2(proposal.toc)));
        }

        let resFP = await addCustomPages(proposal.customPages, 'frontPage', pageStartMap['frontPageExtra'], proposal.style);
        sections.push(...resFP.sections);
    }

    const order = proposal.sectionOrder || ['orgChart', 'resume', 'comparable', 'approach'];

    for (const key of order) {
        if (key === 'orgChart' && proposal.orgChart && isOrgChartReady(proposal.orgChart)) {
            if (proposal.orgChart.template === 1) sections.push(...(await createOrgChartTemp1(proposal.orgChart, pageStartMap.orgChart)));
            else if (proposal.orgChart.template === 2) sections.push(...(await createOrgChartTemp2(proposal.orgChart, pageStartMap.orgChart)));
            else if (proposal.orgChart.template === 3) sections.push(...(await createOrgChartTemp3(proposal.orgChart, pageStartMap.orgChart)));

            let resOC = await addCustomPages(proposal.customPages, 'orgChart', pageStartMap['orgChartExtra'], proposal.style);
            sections.push(...resOC.sections);
        } else if (key === 'resume' && proposal.resume && isResumeReady(proposal.resume)) {
            if (proposal.resume.template === 1) sections.push(...(await createResumeTemp1(proposal.resume, pageStartMap.resume)));
            else if (proposal.resume.template === 2) sections.push(...(await createResumeTemp2(proposal.resume, pageStartMap.resume)));
            else if (proposal.resume.template === 3) sections.push(...(await createResumeTemp3(proposal.resume, pageStartMap.resume)));
            else if (proposal.resume.template === 4) sections.push(...(await createResumeTemp4(proposal.resume, pageStartMap.resume)));

            let resRes = await addCustomPages(proposal.customPages, 'resume', pageStartMap['resumeExtra'], proposal.style);
            sections.push(...resRes.sections);
        } else if (key === 'comparable' && proposal.comparable && isComparableReady(proposal.comparable)) {
            if (proposal.comparable.template === 1) sections.push(...(await createCPTemp1(proposal.comparable, pageStartMap.comparable)));
            else if (proposal.comparable.template === 2) sections.push(...(await createCPTemp2(proposal.comparable, pageStartMap.comparable)));
            else if (proposal.comparable.template === 3) sections.push(...(await createCPTemp3(proposal.comparable, pageStartMap.comparable)));
            else if (proposal.comparable.template === 4) sections.push(...(await createCPTemp4(proposal.comparable, pageStartMap.comparable)));

            let resCP = await addCustomPages(proposal.customPages, 'comparable', pageStartMap['comparableExtra'], proposal.style);
            sections.push(...resCP.sections);
        } else if (key === 'approach' && proposal.approach && isApproachReady(proposal.approach)) {
            if (proposal.approach.template === 1) sections.push(...(await createApprTemp1(proposal.approach, pageStartMap.approach)));
            else if (proposal.approach.template === 2) sections.push(...(await createApprTemp2(proposal.approach, pageStartMap.approach)));
            else if (proposal.approach.template === 3) sections.push(...(await createApprTemp3(proposal.approach, pageStartMap.approach)));

            let resAppr = await addCustomPages(proposal.customPages, 'approach', pageStartMap['approachExtra'], proposal.style);
            sections.push(...resAppr.sections);
        }
    }

    // --- Last Page ---
    if (proposal.lastPage && isLastPageReady(proposal.lastPage)) {
        if (proposal.lastPage.template === 1) sections.push(await createLPTemp1(proposal.lastPage));
        else if (proposal.lastPage.template === 2) sections.push(await createLPTemp2(proposal.lastPage));
        else if (proposal.lastPage.template === 3) sections.push(await createLPTemp3(proposal.lastPage));
        else if (proposal.lastPage.template === 4) sections.push(await createLPTemp4(proposal.lastPage));

    }

    // --- Create Document ---
    const doc = new Document({
        sections: sections
    });

    return await Packer.toBlob(doc);
};
